import React, { useEffect, useState, useMemo } from "react";
import { useParams, useHistory } from "react-router";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { FormattedMessage, useIntl } from "react-intl";

import {
  Button,
  Module,
  ModuleBody,
  ModuleHeader,
  ModuleFooter,
  Wrapper,
  InlineLoading,
} from "@wfp/ui";

import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  requestCurrentVendorsImport,
  requestVendorsImportRecords,
  getVendorsImports,
  getVendorsImportRecords,
} from "data-handler/ducks/vendors";

import Table from "components/CountryAdmin/CountryAdminContent/Table/Table";
import { statusOptions, messageLevelOptions } from "./constants";

import "./_content.scss";

const VendorsImportDetailedPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();

  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { vendorsImportId } = useParams();

  const currentVendorsImport = useSelector(getVendorsImports).results[
    vendorsImportId
  ];

  const vendorsImportRecords = useSelector(getVendorsImportRecords);

  const { count = 0, ids = [] } = vendorsImportRecords;

  const data = ids.map((id) => vendorsImportRecords.results[id]);

  useEffect(() => {
    dispatch(requestCurrentVendorsImport(vendorsImportId, history));
    dispatch(
      requestVendorsImportRecords(pageSize, pageNum, "", vendorsImportId)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorsImportId, dispatch]);

  const fields = [
    {
      id: "csvFilename",
      label: (
        <FormattedMessage
          id="countryAdmin.csvFilename"
          defaultMessage="CSV filename"
        />
      ),
      value: currentVendorsImport?.csv_filename,
    },
    {
      id: "createdBy",
      label: (
        <FormattedMessage
          id="countryAdmin.createdBy"
          defaultMessage="Created by"
        />
      ),
      value: currentVendorsImport?.created_by?.email,
    },
    {
      id: "status",
      label: (
        <FormattedMessage id="countryAdmin.status" defaultMessage="Status" />
      ),
      value: statusOptions.find(
        (item) => item.value === currentVendorsImport?.status
      )?.label,
    },
    {
      id: "numCreated",
      label: (
        <FormattedMessage id="countryAdmin.created" defaultMessage="Created" />
      ),
      value: currentVendorsImport?.num_created,
    },
    {
      id: "numUpdated",
      label: (
        <FormattedMessage id="countryAdmin.updated" defaultMessage="Updated" />
      ),
      value: currentVendorsImport?.num_updated,
    },
    {
      id: "numFailed",
      label: (
        <FormattedMessage id="countryAdmin.failed" defaultMessage="Failed" />
      ),
      value: currentVendorsImport?.num_failed,
    },
    {
      id: "total",
      label: (
        <FormattedMessage id="countryAdmin.total" defaultMessage="Total" />
      ),
      value: currentVendorsImport?.total,
    },
  ];

  const statusCell = ({ row }) => {
    const status = row["original"]["status"];
    return statusOptions.find((item) => item.value === status)?.label;
  };

  const messageLevelCell = ({ row }) => {
    const messageLevel = row["original"]["message_level"];
    return messageLevelOptions.find((item) => item.value === messageLevel)
      ?.label;
  };

  const columns = useMemo(() => {
    return [
      {
        Header: intl.formatMessage({
          id: "countryAdmin.recordNumber",
          defaultMessage: "Record number",
        }),
        accessor: "record_num",
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.status",
          defaultMessage: "Status",
        }),
        accessor: "status",
        Cell: statusCell,
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.message",
          defaultMessage: "Message",
        }),
        accessor: "message",
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.messageLevel",
          defaultMessage: "Message level",
        }),
        accessor: "message_level",
        Cell: messageLevelCell,
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.vendorName",
          defaultMessage: "Vendor name",
        }),
        accessor: "vendor.name",
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.vendorCountry",
          defaultMessage: "Vendor country",
        }),
        accessor: "vendor.country.name",
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPage = (size, idx, ordering) => {
    setPageSize(size);
    setPageNum(idx);
    dispatch(requestVendorsImportRecords(size, idx, ordering, vendorsImportId));
  };

  const tableProps = {
    columns,
    data,
    pageNum,
    pageSize,
    totalItems: count,
    loadPage,
  };

  const downloadFile = () => {
    if (
      currentVendorsImport?.id &&
      currentVendorsImport?.csv_file &&
      currentVendorsImport?.csv_filename
    ) {
      const element = document.createElement("a");

      element.href = currentVendorsImport.csv_file;
      element.download = currentVendorsImport.csv_filename;

      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  if (!currentVendorsImport) {
    return (
      <div className="inlineLoading">
        <InlineLoading />
      </div>
    );
  }

  return (
    <div className="vendors-import-page">
      <Wrapper
        pageWidth="sm"
        spacing="md"
        background="lighter"
        className="table"
      >
        <Module>
          <ModuleHeader className="vendors-import-page-row">
            <Link
              exact="true"
              to={"/countryAdmin/vendors/vendorsImports"}
              style={{ paddingRight: "10px" }}
            >
              <FontAwesomeIcon icon={faChevronLeft} size="lg" />
            </Link>
            {currentVendorsImport?.csv_filename}
          </ModuleHeader>
          <ModuleBody>
            <div className="vendors-import-page">
              {fields.map((field) => (
                <div key={field.id} className="vendors-import-page-row">
                  <label className="wfp--label">{field.label}</label>
                  <span className="field-value">{field.value}</span>
                </div>
              ))}
              {!data.find(
                (item) => item.vendor_import !== parseInt(vendorsImportId)
              ) &&
                currentVendorsImport?.total > 0 &&
                data.length > 0 && <Table {...tableProps} showSort />}
            </div>
          </ModuleBody>
          <ModuleFooter>
            <Button kind="secondary" onClick={downloadFile}>
              <FormattedMessage
                id="countryAdmin.downloadCsvFile"
                defaultMessage="Download CSV file"
              />
            </Button>
          </ModuleFooter>
        </Module>
      </Wrapper>
    </div>
  );
};

export default VendorsImportDetailedPage;
